// src/features/assessment/data/results.transformer.ts

import { AssessmentResult } from '../engine/types';

export const TRANSFORMER_RESULTS: Record<string, AssessmentResult> = {
  
  // ==========================================
  // RESULT 1: THE ARCHITECT (High Maturity)
  // ==========================================
  transformer_architect: {
    id: 'transformer_architect',
    track: 'transformer',
    title: "The Architect",
    verdict: "Ready for Sovereignty",
    hero_image: "/images/illustrations/results/architect_hero.avif",
    
    diagnosis: {
      headline: "You have the 'Digital Signal' required for high-velocity automation.",
      text: "Your organization has passed the 'Digital Threshold.' Unlike 70% of companies we audit, your team uses modern tools (Linear, Slack, HubSpot) and your decisions are documented. You have the raw material for intelligence. However, you are paying a massive efficiency tax. Your highly paid seniors are spending 30-50% of their time acting as 'Human Routers'—moving context from one tool to another."
    },
    
    solution: {
      headline: "The Company AI Computer",
      text: "We propose building a sovereign, air-gapped environment that sits *above* your current tools. It ingests your clean data into a **Central Knowledge Base** that powers every agent. We don't just hand you code; we install **Canvas OS**, the operating system for the AI-First company."
    },

    // DYNAMIC BLOCKS: These change based on the user's "Priority" answer
    dynamic_content: {
      engineering: {
        title: "Your Velocity Track",
        objective: "Shift Senior Engineers from 'Writing Boilerplate' to 'Reviewing Architecture'. Reduce Cycle Time by 60%.",
        roadmap_title: "The Vision: The Real-Time Sprint",
        steps: [
          { phase: "10:00 AM", name: "The Meeting", detail: "The PM Agent transcribes requirements into a structured PRD." },
          { phase: "10:30 AM", name: "Real-Time Architect", detail: "The Architect Agent flags database conflicts immediately." },
          { phase: "12:00 PM", name: "The Build", detail: "The Coder Agent spins up, generates boilerplate, and pushes to branch." },
          { phase: "01:00 PM", name: "The Handoff", detail: "You return to a Live Preview. The QA Agent has already run the suite." }
        ],
        read_more: { title: "Read: The Self-Updating Spec", slug: "self-updating-spec-documentation" }
      },
      sales: {
        title: "Your Revenue Track",
        objective: "Transform Sales Teams from 'Data Entry Clerks' into 'Strategic Consultants'.",
        roadmap_title: "The Agentic Deal Flow",
        steps: [
          { phase: "Pre-Call", name: "The Research Agent", detail: "Builds a customized slide deck pre-filled with prospect logo/data." },
          { phase: "Live", name: "The Concierge", detail: "Participates in call, pulling technical specs on demand." },
          { phase: "Post-Call", name: "The Closer", detail: "Transcribes call, updates Salesforce, drafts follow-up, and nurtures." }
        ],
        read_more: { title: "Read: The Death of Smile and Dial", slug: "death-of-smile-and-dial" }
      },
      marketing: {
        title: "Your Growth Track",
        objective: "Build an 'Autonomous Newsroom'. Move from Content Scarcity to Content Abundance.",
        roadmap_title: "The Asset Factory",
        steps: [
          { phase: "Input", name: "The Concept", detail: "One core idea (e.g. Founder Video) becomes a global campaign." },
          { phase: "Scale", name: "The Content Engine", detail: "Transcribes video and generates Blog, LinkedIn, and Newsletter." },
          { phase: "Video", name: "The Video Lab", detail: "Automatically cuts vertical Shorts and dubs into 5 languages." }
        ],
        read_more: { title: "Read: The Autonomous Newsroom", slug: "autonomous-newsroom" }
      },
      ops: {
        title: "Your Backbone Track",
        objective: "Make Operations invisible. Reduce 'Internal Support Tickets' to near zero.",
        roadmap_title: "The Frictionless Office",
        steps: [
          { phase: "Hiring", name: "The Recruiter Agent", detail: "Filters resumes based on portfolio quality, not keywords." },
          { phase: "Start", name: "The Onboarding Agent", detail: "Acts as concierge for new hires (I-9s, Software Access)." },
          { phase: "Daily", name: "The Employee Companion", detail: "Private agent answers policy questions ('How do I expense this?') instantly." }
        ],
        read_more: { title: "Read: The End of HR Admin", slug: "end-of-hr-admin" }
      },
      // Fallback if no department selected (shouldn't happen, but good for TS safety)
      default: {
        title: "The Architecture Track",
        objective: "Deploy Sovereign Agents.",
        roadmap_title: "Implementation",
        steps: [],
      }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The End of SaaS", subtitle: "Why you need a central computer.", slug: "the-end-of-saas" },
        { title: "The 48-Hour Blueprint", subtitle: "How we map your future.", slug: "48-hour-blueprint-ai-audit" }
      ]
    },

    cta: {
      primary: { label: "Secure Strategy Session", variant: "book", cal_params: { note: "Architect Qualified" } },
      secondary: { label: "Save Roadmap", variant: "save" }
    }
  },

  // ==========================================
  // RESULT 2: THE CLEAN-UP PHASE (Medium Maturity)
  // ==========================================
  transformer_cleanup: {
    id: 'transformer_cleanup',
    track: 'transformer',
    title: "The Clean-Up Phase",
    verdict: "High Potential, Low Structure",
    hero_image: "/images/illustrations/results/cleanup_hero.avif",
    
    diagnosis: {
      headline: "You have the right tools, but you lack the Architecture.",
      text: "Your data is siloed and your documentation is stale. This is why buying generic AI subscriptions (like Copilot) hasn't solved your problems yet—the AI is hallucinating because the source data is confusing. **You cannot automate chaos.**"
    },
    
    solution: {
      headline: "Map and Clean Before You Build",
      text: "We need to structure your data before we deploy agents. We recommend a **'Pilot Project' Strategy**—solving one specific workflow first to create a Center of Excellence."
    },

    dynamic_content: {
      engineering: {
        title: "The Repo Clean-Up",
        objective: "Build trust in the system without risking production code.",
        roadmap_title: "Pilot: The Archivist",
        steps: [
          { phase: "Step 1", name: "The Scan", detail: "We deploy an Archivist Agent to scan your Repo and Docs." },
          { phase: "Step 2", name: "The Sync", detail: "The agent identifies discrepancies ('Docs say v2, Code says v3')." },
          { phase: "Step 3", name: "The QA Runner", detail: "Once docs are clean, we enable automated test generation." }
        ],
        read_more: { title: "Read: The Amnesia Problem", slug: "amnesia-problem-ai-memory" }
      },
      sales: {
        title: "The CRM Hygiene Fix",
        objective: "AI Sales Agents fail if they can't trust the contact info.",
        roadmap_title: "Pilot: The Glass Box",
        steps: [
          { phase: "Step 1", name: "The Hygiene Agent", detail: "Scans Salesforce to deduplicate leads and fix missing fields." },
          { phase: "Step 2", name: "Enrichment", detail: "Cross-references LinkedIn to verify decision makers." },
          { phase: "Step 3", name: "Outbound Pilot", detail: "Turn on the Sequencer for a small, trusted cohort." }
        ],
        read_more: { title: "Read: The Glass Box", slug: "glass-box-forensic-observability" }
      },
      marketing: {
        title: "The Brand Audit",
        objective: "Prevent the 'Generic AI Slop' feeling in your content.",
        roadmap_title: "Pilot: The Brand Brain",
        steps: [
          { phase: "Step 1", name: "Ingest", detail: "Organize Brand Assets (Voice, Tone, Logos) into a Knowledge Graph." },
          { phase: "Step 2", name: "Train", detail: "Teach the Content Engine your specific voice." },
          { phase: "Step 3", name: "Generate", detail: "Produce assets that actually sound like you." }
        ],
        read_more: { title: "Read: The Autonomous Newsroom", slug: "autonomous-newsroom" }
      },
      ops: {
        title: "The Policy Digitization",
        objective: "Stop the repetitive Slack questions that slow down your leaders.",
        roadmap_title: "Pilot: The Policy Center",
        steps: [
          { phase: "Step 1", name: "Ingest", detail: "Digitize 'Tribal Knowledge' into a structured Policy Center." },
          { phase: "Step 2", name: "Resolve", detail: "Fix contradictions between Slack history and PDF handbooks." },
          { phase: "Step 3", name: "Serve", detail: "Launch the Chief of Staff node for instant employee answers." }
        ],
        read_more: { title: "Read: The End of HR Admin", slug: "end-of-hr-admin" }
      },
      default: { title: "Pilot Strategy", objective: "Clean Data", roadmap_title: "Steps", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The Amnesia Problem", subtitle: "Why your docs are failing you.", slug: "amnesia-problem-ai-memory" },
        { title: "The Stupid Agent Trap", subtitle: "Why structure matters.", slug: "stupid-agent-trap-knowledge-base" }
      ]
    },

    cta: {
      primary: { label: "Book the Audit to Get the Map", variant: "book", cal_params: { note: "Cleanup Phase" } },
      secondary: { label: "Save Roadmap", variant: "save" }
    }
  },

  // ==========================================
  // RESULT 3: THE DIGITAL TRANSITION (Low Maturity)
  // ==========================================
  transformer_digital: {
    id: 'transformer_digital',
    track: 'transformer',
    title: "The Digital Transition",
    verdict: "Early Stage Digitization",
    hero_image: "/images/illustrations/results/digital_hero.avif",
    
    diagnosis: {
      headline: "You have a Signal Problem.",
      text: "Your company currently relies on 'Verbal Culture' (Hallway chats, phone calls). This makes you invisible to AI. An Agent cannot automate what it cannot see. Furthermore, your team's tolerance for error is low."
    },
    
    solution: {
      headline: "The 'Sidecar' System",
      text: "We should not try to automate your core 'Human' processes yet. Instead, we build a parallel **Sidecar System** that runs digitally and hands off results to your humans. This allows you to get AI benefits without forcing your entire team to change their habits overnight."
    },

    dynamic_content: {
      sales: {
        title: "The Lead Gen Sidecar",
        objective: "Prove value without risk.",
        roadmap_title: "The Sidecar Strategy",
        steps: [
          { phase: "Step 1", name: "The Engine", detail: "We build a digital engine that finds leads and warms them up via email." },
          { phase: "Step 2", name: "The Handoff", detail: "It hands you a 'Hot List'. You keep closing on the phone." },
          { phase: "Step 3", name: "Integration", detail: "Once you trust the data, we connect it to CRM." }
        ],
        read_more: { title: "Read: The Death of Smile and Dial", slug: "death-of-smile-and-dial" }
      },
      marketing: {
        title: "The Social Sidecar",
        objective: "Active social media without hiring a manager.",
        roadmap_title: "The Content Sidecar",
        steps: [
          { phase: "Step 1", name: "The Input", detail: "You record a voice note or Loom video explaining an idea." },
          { phase: "Step 2", name: "The Engine", detail: "Sidecar transcribes, formats, and creates visual assets." },
          { phase: "Step 3", name: "The Approval", detail: "You click 'Yes' to post." }
        ],
        read_more: { title: "Read: The Autonomous Newsroom", slug: "autonomous-newsroom" }
      },
      ops: {
        title: "The Knowledge Sidecar",
        objective: "Stop answering the same questions repeatedly.",
        roadmap_title: "The Shadow Brain",
        steps: [
          { phase: "Step 1", name: "Digitization", detail: "We interview your experts and record their answers." },
          { phase: "Step 2", name: "Interface", detail: "We give your team a simple 'Search Bar' for company policy." },
          { phase: "Step 3", name: "Training", detail: "Turn the brain into an Onboarding Agent for new hires." }
        ],
        read_more: { title: "Read: The End of HR Admin", slug: "end-of-hr-admin" }
      },
      engineering: {
        title: "The Ticket Adapter",
        objective: "Digitize the signal.",
        roadmap_title: "The Triage Sidecar",
        steps: [
          { phase: "Step 1", name: "The Setup", detail: "AI cannot help without tickets. We set up Linear/Jira." },
          { phase: "Step 2", name: "The Adapter", detail: "Install a 'Triage Agent' to organize work automatically." }
        ],
        read_more: { title: "Read: The Self-Updating Spec", slug: "self-updating-spec-documentation" }
      },
      default: { title: "Sidecar Strategy", objective: "Digitize", roadmap_title: "Steps", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The End of SaaS", subtitle: "Why digitization matters.", slug: "the-end-of-saas" }
      ]
    },

    cta: {
      primary: { label: "Book a Consultation", variant: "consultation", cal_params: { note: "Digital Transition Strategy" } },
      secondary: { label: "Save Roadmap", variant: "save" }
    }
  }
};